<template>
    <div id="customLoginForm">
        <div>
            <b-row v-if="!customInputsIsValid">
                <b-col lg="12">
                    <a-alert :key="`alertContainer-${$i18n.locale}`" class="mb-2" showIcon type="warning">
                        <div slot="description">
                            {{ $t("message.customLoginAlert") }}
                        </div>
                    </a-alert>
                </b-col>
            </b-row>
            <b-row>
                <b-col lg="4" style="border-right: 2px solid gray">
                    <draggable :sort="false">
                        <md-button
                            v-for="(field, i) in formInputs"
                            :key="i"
                            v-tippy="{
                                followCursor: true,
                                placement: 'bottom-end',
                                theme: isDarkMode ? 'light-border' : 'google',
                            }"
                            :content="getFieldDescription(field.type)"
                            :disabled="isDisable(field.type)"
                            class="fields p-0 m-0"
                            @click="addNewInput(field)"
                        >
                            <md-icon>add</md-icon>
                            <span>{{
                                    field.type === "subject" ? $t("message.department") : $t("message." + field.type)
                                }}</span>
                        </md-button>
                    </draggable>
                </b-col>
                <b-col lg="8">
                    <draggable id="list" :list="customInputs" group="types" @change="dragChange">
                        <div v-for="(field, i) in customInputs" :key="i" class="fields">
                            <div class="feature">
                                <div class="left">
                                    <div class="texts">
                                        <div class="feature-title">
                                            <div
                                                v-tippy="{
                                                    followCursor: true,
                                                    placement: 'bottom-end',
                                                    theme: isDarkMode ? 'light-border' : 'google',
                                                }"
                                                :content="$t('message.doubleClickEdit')"
                                                class="infoF"
                                                v-on:dblclick="handleEdit(field)"
                                            >
                                                <md-icon>drag_handle</md-icon>
                                                <span v-if="field.type !== 'label'" v-html="getLocalizedLabel(field)"></span>
                                                :
                                                <span>{{
                                                        field.type === "subject"
                                                            ? $t("message.department")
                                                            : $t("message." + field.type)
                                                    }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="right">
                                    <div class="actions">
                                        <md-button
                                            v-tippy="{
                                                followCursor: true,
                                                placement: 'bottom-end',
                                                theme: isDarkMode ? 'light-border' : 'google',
                                            }"
                                            :content="
                                                field.visible
                                                    ? $t('message.visibleField')
                                                    : $t('message.notVisibleField')
                                            "
                                            @click="toggleProperty(field, 'visible')"
                                        >
                                            <md-icon
                                                :style="{
                                                    color: field.visible ? 'green' : 'gray',
                                                }"
                                            >
                                                {{ field.visible ? "visibility" : "visibility_off" }}
                                            </md-icon>
                                        </md-button>
                                        <md-button
                                            v-tippy="{
                                                followCursor: true,
                                                placement: 'bottom-end',
                                                theme: isDarkMode ? 'light-border' : 'google',
                                            }"
                                            :content="
                                                field.required
                                                    ? $t('message.requiredField')
                                                    : $t('message.notRequiredField')
                                            "
                                            @click="toggleProperty(field, 'required')"
                                        >
                                            <md-icon
                                                :style="{
                                                    color: field.required ? 'goldenrod' : 'gray',
                                                }"
                                            >priority_high
                                            </md-icon>
                                        </md-button>
                                        <md-button
                                            v-tippy="{
                                                followCursor: true,
                                                placement: 'bottom-end',
                                                theme: isDarkMode ? 'light-border' : 'google',
                                            }"
                                            :content="$t('message.edit')"
                                            class="md-primary"
                                            @click="handleEdit(field)"
                                        >
                                            <md-icon>edit</md-icon>
                                        </md-button>
                                        <md-button
                                            v-tippy="{
                                                followCursor: true,
                                                placement: 'bottom-end',
                                                theme: isDarkMode ? 'light-border' : 'google',
                                            }"
                                            :content="$t('message.delete')"
                                            class="md-accent"
                                            @click="handleDelete(field._id)"
                                        >
                                            <md-icon>delete</md-icon>
                                        </md-button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div slot="footer">
                            <button class="addButton" @click="addNewInput">
                                <strong>+</strong>
                                {{ $t("message.addNewField") }}
                            </button>
                        </div>
                    </draggable>
                </b-col>
            </b-row>
        </div>

        <Modal2
            v-if="isModalShow"
            id="newFieldModal"
            :title="$t(editMode ? 'message.editField' : 'message.addNewField')"
            :show-footer="false"
            @cancel="hideModal"
        >
            <div id="inner-modal">
                <a-alert
                    v-if="($validator.errors.items.length || !isOneLanguageIncludes()) && submitClicked"
                    :message="$t('message.error')"
                    class="mb-2"
                    showIcon
                    type="error"
                >
                    <div slot="description">
                        <div v-for="err of $validator.errors.items">
                            <span>
                                {{ err.msg }}
                            </span>
                        </div>
                        <div v-if="!isOneLanguageIncludes()">
                            <span>
                                {{ $t("message.atLeastOneLanguage") }}
                            </span>
                        </div>
                    </div>
                </a-alert>
                <b-form @submit.prevent="handleSubmit">
                    <b-row class="mb-1">
                        <b-col>
                            <label>{{ $t("message.name") }}</label>
                            <b-form-group>
                                <b-form-input
                                    :disabled="['email', 'fullname', 'language'].includes(input.type)"
                                    v-model="input.name"
                                    v-validate="'required'"
                                    :name="$t('message.name')"
                                    trim
                                ></b-form-input>
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <label>{{ $t("message.type") }}</label>
                            <b-form-group>
                                <b-form-select
                                    v-model="input.type"
                                    name="type"
                                    :disabled="['email', 'fullname', 'language'].includes(input.type)"
                                >
                                    <option value="text">
                                        {{ $t("message.text") }}
                                    </option>
                                    <option value="label">
                                        {{ $t("message.label") }}
                                    </option>
                                    <option value="check">
                                        {{ $t("message.check") }}
                                    </option>
                                    <option value="numeric">
                                        {{ $t("message.numeric") }}
                                    </option>
                                    <option value="phone">
                                        {{ $t("message.phone") }}
                                    </option>
                                    <option value="select">
                                        {{ $t("message.select") }}
                                    </option>
                                    <option value="city">
                                        {{ $t("message.city") }}
                                    </option>
                                    <option value="cityAndDistrict">
                                        {{ $t("message.cityDistrict") }}
                                    </option>
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                    </b-row>
                    <b-row class="mb-1">
                        <b-col>
                            <label>Eşleştir</label>
                            <b-form-group>
                                <b-form-select v-model="input.contactProperty" name="key">
                                    <option :value="null" default>-</option>
                                    <option v-for="attribute of visitorAttributes" :value="attribute._id">
                                        {{ attribute.label }}
                                    </option>
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                        <b-col></b-col>
                    </b-row>
                    <b-row class="mb-1">
                        <b-col>
                            <span id="visibilityid">{{ $t("message.visible") }}</span>
                            <md-switch v-model="input.visible" name="visible"></md-switch>
                        </b-col>
                        <b-col>
                            <span id="requiredid">{{ $t("message.required") }}</span>
                            <md-switch v-model="input.required" name="required"></md-switch>
                        </b-col>
                    </b-row>
                    <MultiContent
                        v-if="input.type === 'label'"
                        v-model="inputInitialObjectContent"
                        ref="MultiContent"
                    />
                    <b-tabs v-else class="mb-1">
                        <label class="pt-1">{{ $t("message.label") }}</label>
                        <b-form-group class="mt-1">
                            <MultiContent v-model="inputInitialObjectTitle" ref="MultiContent" />
                        </b-form-group>
                    </b-tabs>
                    <div
                        v-if="input.type === 'select'"
                        style="max-height: 300px; overflow-y: scroll; overflow-x: hidden"
                    >
                        <b-row class="mt-1">
                            <b-col>
                                <label>{{ $t("message.options") }}</label>
                                <b-row v-for="(option, i) in input.options" :key="i">
                                    <b-col class="d-flex align-items-center">
                                        <b-form-group>
                                            <b-form-radio
                                                v-model="input.defaultOption"
                                                :value="input.options[i]"
                                                name="defaultOption"
                                            />
                                        </b-form-group>

                                        <b-form-group class="w-100">
                                            <b-form-input
                                                v-model="input.options[i]"
                                                v-validate="'required'"
                                                :name="'options-' + i"
                                                :placeholder="$t('message.optionsPlaceholder')"
                                                trim
                                            />
                                        </b-form-group>

                                        <md-button
                                            class="mb-4"
                                            style="width: 42px; min-width: 42px"
                                            @click="input.options.splice(i, 1)"
                                        >
                                            <md-icon>delete</md-icon>
                                        </md-button>
                                    </b-col>
                                </b-row>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <md-button @click="input.options.push('')">
                                    <md-icon>add</md-icon>
                                </md-button>
                            </b-col>
                        </b-row>
                    </div>

                    <b-col md="12">
                        <div id="buttons">
                            <b-button @click="hideModal">
                                {{ $t("message.cancel") }}
                            </b-button>

                            <b-button type="submit" variant="primary">
                                {{ editMode ? $t("message.update") : $t("message.save") }}
                            </b-button>
                        </div>
                    </b-col>
                </b-form>
            </div>
        </Modal2>
    </div>
</template>

<script>
import draggable from "vuedraggable";
import { mapActions, mapGetters, mapState } from "vuex";
import _ from "lodash";
import createLogger from "../utils/log2";
import MultiContent from "./MultiContent";
import { toArray2, toObject2 } from "../utils/languageContent";
import Modal2 from "./common/Modal2.vue";

const log = createLogger("CustomLoginForm");

const createInitialInput = (type) => ({
    name: "",
    required: false,
    visible: true,
    order: 0,
    type,
    contactProperty: null,
    localizedLabel: {
        tr: {
            text: "",
            languageCode: "tr",
        },
        en: {
            text: "",
            languageCode: "en",
        },
        ru: {
            text: "",
            languageCode: "ru",
        },
        de: {
            text: "",
            languageCode: "de",
        },
        fr: {
            text: "",
            languageCode: "fr",
        },
        es: {
            text: "",
            languageCode: "es",
        },
        ar: {
            text: "",
            languageCode: "ar",
        },
        it: {
            text: "",
            languageCode: "it",
        },
    },
});
export default {
    name: "CustomLoginForm",
    components: {
        MultiContent,
        draggable,
        Modal2,
    },
    data() {
        return {
            formInputs: [
                {
                    name: "Fullname",
                    required: true,
                    visible: true,
                    order: 0,
                    type: "fullname",
                    localizedLabel: {
                        tr: {
                            languageCode: "tr",
                            text: "Ad Soyad",
                        },
                        en: {
                            languageCode: "en",
                            text: "Fullname",
                        },
                        ru: {
                            languageCode: "ru",
                            text: "ФИО",
                        },
                        de: {
                            languageCode: "de",
                            text: "Vorname Familienname",
                        },
                        fr: {
                            languageCode: "fr",
                            text: "Prénom / nom de famille",
                        },
                        es: {
                            languageCode: "es",
                            text: "Nombre Apellido",
                        },
                        ar: {
                            languageCode: "ar",
                            text: "اسم اللقب",
                        },
                        it: {
                            languageCode: "it",
                            text: "Nome Cognome",
                        },
                    },
                },
                {
                    name: "Email",
                    required: true,
                    visible: true,
                    order: 0,
                    type: "email",
                    localizedLabel: {
                        tr: {
                            languageCode: "tr",
                            text: "E-Posta",
                        },
                        en: {
                            languageCode: "en",
                            text: "Email",
                        },
                        ru: {
                            languageCode: "ru",
                            text: "е-мейл",
                        },
                        de: {
                            languageCode: "de",
                            text: "E-Mail",
                        },
                        fr: {
                            languageCode: "fr",
                            text: "E-Mail",
                        },
                        es: {
                            languageCode: "es",
                            text: "Correo electrónico",
                        },
                        ar: {
                            languageCode: "ar",
                            text: "بريد إلكتروني",
                        },
                        it: {
                            languageCode: "it",
                            text: "E-mail",
                        },
                    },
                },
                {
                    name: "Language",
                    required: true,
                    visible: true,
                    order: 0,
                    type: "language",
                    localizedLabel: {
                        tr: {
                            languageCode: "tr",
                            text: "Konuşma Dili",
                        },
                        en: {
                            languageCode: "en",
                            text: "Language",
                        },
                        ru: {
                            languageCode: "ru",
                            text: "язык",
                        },
                        de: {
                            languageCode: "de",
                            text: "Sprache",
                        },
                        fr: {
                            languageCode: "fr",
                            text: "Langue",
                        },
                        es: {
                            languageCode: "es",
                            text: "Idioma",
                        },
                        ar: {
                            languageCode: "ar",
                            text: "لغة",
                        },
                        it: {
                            languageCode: "it",
                            text: "Lingua",
                        },
                    },
                },
                {
                    name: "",
                    required: false,
                    visible: true,
                    order: 0,
                    type: "text",
                    localizedLabel: {},
                },
                {
                    name: "",
                    required: false,
                    visible: true,
                    order: 0,
                    type: "numeric",
                    localizedLabel: {},
                },
                {
                    name: "",
                    required: false,
                    visible: true,
                    order: 0,
                    type: "phone",
                    localizedLabel: {},
                },
                {
                    name: "",
                    required: false,
                    visible: true,
                    order: 0,
                    type: "select",
                    options: [""],
                    defaultOption: "",
                    localizedLabel: {},
                },
                {
                    name: "subject",
                    required: true,
                    visible: true,
                    order: 0,
                    type: "subject",
                    localizedLabel: {
                        tr: {
                            languageCode: "tr",
                            text: "Departman",
                        },
                        en: {
                            languageCode: "en",
                            text: "Department",
                        },
                        ru: {
                            languageCode: "ru",
                            text: "тема",
                        },
                        de: {
                            languageCode: "de",
                            text: "Abteilung",
                        },
                        fr: {
                            languageCode: "fr",
                            text: "Département",
                        },
                        es: {
                            languageCode: "es",
                            text: "Departamento",
                        },
                        ar: {
                            languageCode: "ar",
                            text: "قسم",
                        },
                        it: {
                            languageCode: "it",
                            text: "Dipartimento",
                        },
                    },
                },
                {
                    name: "City",
                    required: true,
                    visible: true,
                    order: 0,
                    type: "city",
                    localizedLabel: {
                        tr: {
                            languageCode: "tr",
                            text: "Şehir",
                        },
                        en: {
                            languageCode: "en",
                            text: "City",
                        },
                        ru: {
                            languageCode: "ru",
                            text: "город",
                        },
                        de: {
                            languageCode: "de",
                            text: "Stadt",
                        },
                        fr: {
                            languageCode: "fr",
                            text: "Ville",
                        },
                        es: {
                            languageCode: "es",
                            text: "Ciudad",
                        },
                        ar: {
                            languageCode: "ar",
                            text: "مدينة",
                        },
                        it: {
                            languageCode: "it",
                            text: "Città",
                        },
                    },
                },
                {
                    name: "City&District",
                    required: false,
                    visible: true,
                    order: 0,
                    type: "cityAndDistrict",
                    localizedLabel: {
                        tr: {
                            languageCode: "tr",
                            text: "Şehir/İlçe",
                        },
                        en: {
                            languageCode: "en",
                            text: "City/District",
                        },
                        ru: {
                            languageCode: "ru",
                            text: "город/округ",
                        },
                        de: {
                            languageCode: "de",
                            text: "Stadt/Bezirk",
                        },
                        fr: {
                            languageCode: "fr",
                            text: "Ville/District",
                        },
                        es: {
                            languageCode: "es",
                            text: "Ciudad/Pueblo",
                        },
                        ar: {
                            languageCode: "ar",
                            text: "المدينة / البلدة",
                        },
                        it: {
                            languageCode: "it",
                            text: "Città/Quartiere",
                        },
                    },
                },
                {
                    name: "",
                    required: false,
                    visible: true,
                    order: 0,
                    type: "label",
                    localizedLabel: {},
                },
                {
                    name: "",
                    required: false,
                    visible: true,
                    order: 0,
                    type: "check",
                    localizedLabel: {},
                },
            ],
            input: _.cloneDeep(createInitialInput("text")),
            editMode: false,
            submitClicked: false,
            disableButton: false,
            newInputTitle: "",
            isModalShow: false,
        };
    },
    computed: {
        ...mapState({
            languages: (state) => _.sortBy(state.language.languages, "order"),
            customInputs: (state) => state.customLoginForm.inputs,
            customInputsIsValid: (state) => !!state.customLoginForm.inputs.filter((x) => x.visible).length,
            systemLanguages: (state) => state.setting.settings.systemLanguages,
            visitorAttributes: (state) => state.contactProperty.data || [],
        }),
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
        }),

        inputInitialObjectTitle: {
            get() {
                if (!this.systemLanguages) {
                    return [];
                }
                return [
                    {
                        type: "rich",
                        field: "text",
                        label: this.$t("message.message"),
                        placeholder: this.$t("message.message"),
                        data: this.systemLanguages.reduce((acc, lang) => {
                            acc[lang] = _.get(this.input.localizedLabel, `[${lang}].text`, "");
                            return acc;
                        }, {}),
                        validation: "required",
                    },
                ];
            },
            set(v) {
                this.input.localizedLabel = this.systemLanguages.reduce((acc, lang) => {
                    acc[lang] = {};
                    for (const _v of v) acc[lang][_v.field] = _v.data[lang];
                    return acc;
                }, {});
            },
        },
        inputInitialObjectContent: {
            get() {
                if (!this.systemLanguages) {
                    return [];
                }
                return [
                    {
                        type: "rich",
                        field: "text",
                        label: this.$t("message.message"),
                        placeholder: this.$t("message.message"),
                        data: this.systemLanguages.reduce((acc, lang) => {
                            acc[lang] = _.get(this.input.localizedLabel, `[${lang}].text`, "");
                            return acc;
                        }, {}),
                        validation: "required",
                    },
                ];
            },
            set(v) {
                this.input.localizedLabel = this.systemLanguages.reduce((acc, lang) => {
                    acc[lang] = {};
                    for (const _v of v) acc[lang][_v.field] = _v.data[lang];
                    return acc;
                }, {});
            },
        },

        inputContent: {
            get() {
                return toArray2(this.input, "localizedLabel").localizedLabel;
            },
            set(value) {
                this.input.localizedLabel = toObject2(value);
            },
        },
        inputTitleObject: {
            get() {
                return toArray2(this.input, "localizedLabel").localizedLabel;
            },
            set(value) {
                this.input.localizedLabel = toObject2(value);
                this.addNewInputTitle();
            },
        },
    },
    methods: {
        ...mapActions({
            createInput: "customLoginForm/createInput",
            getCustomLoginFormInputs: "customLoginForm/getCustomLoginFormInputs",
            updateInput: "customLoginForm/updateInput",
            multiUpdateInput: "customLoginForm/multiUpdateInput",
            deleteInput: "customLoginForm/deleteInput",
        }),
        showModal() {
            this.isModalShow = true;
        },
        hideModal() {
            this.isModalShow = false;
            this.submitClicked = false;
        },
        getLocalizedLabel(field) {
            const validLangs = Object.keys(field.localizedLabel || {});
            const lang = validLangs.includes(this.$i18n.locale) ? this.$i18n.locale : validLangs[0];
            return _.get(field, `localizedLabel.${lang}.text`, "").toUpperCase();
        },
        titleChangeHandler(titleObj) {
            this.newInputTitle = titleObj;
            this.addNewInputTitle();
        },
        addNewInputTitle() {
            Object.keys(this.newInputTitle).forEach((key) => {
                if (!this.input.localizedLabel[key]) {
                    return (this.input.localizedLabel[key] = {
                        text: this.newInputTitle[key],
                        languageCode: key,
                    });
                }
                this.input.localizedLabel[key].text = this.newInputTitle[key];
            });
        },
        onDeleteLanguage(data) {
            this.input.localizedLabel = toObject2(data.content, "text");
        },
        isOneLanguageIncludes() {
            let field = this.input.localizedLabel;
            return Object.keys(field).some((lang) => field[lang].text !== "");
        },
        dragChange(events) {
            let fixedInputs = this.customInputs.map((x, i) => ({
                ...x,
                order: i,
            }));
            log("Drag change fixedInputs", { fixedInputs });
            this.multiUpdateInput(fixedInputs);
        },
        addNewInput({ type }) {
            this.editMode = false;
            const input = _.cloneDeep(createInitialInput(type || "text"));
            input["order"] = this.customInputs.length;
            if (["email", "fullname", "subject", "language"].includes(type)) {
                let maybeInput = this.formInputs.find((x) => x.type === type);
                if (maybeInput) {
                    input.name = maybeInput.name;
                    input.localizedLabel = maybeInput.localizedLabel;
                    input.required = maybeInput.required;
                    input.visible = maybeInput.visible;
                    input.contactProperty = maybeInput.contactProperty;
                }
                log("inputu logladık", { input });
                this.disableButton = true;
                this.createInput(input).then((response) => {
                    this.disableButton = false;
                });
            } else {
                if (type === "select") {
                    let maybeInput = this.formInputs.find((x) => x.type === "select");
                    if (maybeInput) {
                        maybeInput = _.cloneDeep(maybeInput);
                        input.options = maybeInput.options;
                        input.defaultOption = maybeInput.defaultOption;
                        input.contactProperty = maybeInput.contactProperty;
                    }
                }
                log("inputu logladık", { input });
                this.input = input;
                this.showModal();
                //this.$bvModal.show("newFieldModal");
            }
        },
        handleSubmit() {
            this.submitClicked = true;
            this.$validator.validateAll().then((isSuccess) => {
                if (isSuccess && this.isOneLanguageIncludes()) {
                    this.$refs.MultiContent.validate().then((isMultiContentValid) => {
                        if (isMultiContentValid) {
                            let input = _.cloneDeep(this.input);
                            input.localizedLabel = this.editLocalizedFields(input.localizedLabel);
                            let p;

                            if (!this.editMode) {
                                input["order"] = this.customInputs.length;
                                p = this.createInput(input);
                            } else {
                                p = this.updateInput(input);
                                this.editMode = false;
                            }
                            p.then((x) => {
                                this.$toasted.show(this.$t("message.userSaved"), {
                                    duration: 3000,
                                    position: "bottom-center",
                                });
                            }).catch((err) => {
                                const errMsg =
                                    err.response && err.response.data && err.response.data.localizedMessages
                                        ? err.response.data.localizedMessages[this.$i18n.locale]
                                        : err.message;
                                this.$toasted.error(errMsg, {
                                    duration: 4000,
                                    position: "bottom-center",
                                });
                            });
                            this.input = _.cloneDeep(createInitialInput("text"));
                            this.hideModal();
                            //this.$bvModal.hide("newFieldModal");
                        }
                    });
                }
            });
        },
        editLocalizedFields(field) {
            let find = Object.keys(field).find((lang) => field[lang].text !== "");
            if (find) {
                let empties = Object.keys(field).filter((lang) => field[lang].text === "");
                empties.forEach((item) => (field[item].text = field[find].text));
                log.debug({
                    field,
                    find,
                    empties,
                });
            }
            return field;
        },
        async handleDelete(id) {
            if (id) {
                await this.deleteInput(id);
                let fixedInputs = _.cloneDeep(this.customInputs);
                fixedInputs = fixedInputs.map((x, i) => ({
                    ...x,
                    order: i,
                }));
                await this.multiUpdateInput(fixedInputs);
            }
        },
        handleEdit(field) {
            this.editMode = true;
            this.input = _.cloneDeep(field);
            //this.$bvModal.show("newFieldModal");
            this.showModal();
        },
        moveUp(field) {
            let inputs = this.customInputs;
            const findIndex = inputs.findIndex((x) => x._id === field._id);
            const temp = inputs[findIndex];
            inputs[findIndex] = inputs[findIndex - 1];
            inputs[findIndex - 1] = temp;
            let fixedInputs = inputs.map((x, i) => ({ ...x, order: i }));
            this.multiUpdateInput(fixedInputs);
        },
        toggleProperty(field, property) {
            this.editMode = true;
            let input = _.cloneDeep(field);
            input[property] = !input[property];
            switch (property) {
                case "required":
                    if (input.required) input.visible = true;
                    break;
                case "visible":
                    if (!input.visible) input.required = false;
                    break;
            }
            this.updateInput(input);
        },
        moveDown(field) {
            let inputs = this.customInputs;
            const findIndex = inputs.findIndex((x) => x._id === field._id);
            const temp = inputs[findIndex];
            inputs[findIndex] = inputs[findIndex + 1];
            inputs[findIndex + 1] = temp;
            let fixedInputs = inputs.map((x, i) => ({ ...x, order: i }));
            this.multiUpdateInput(fixedInputs);
        },
        isDisable(type) {
            return (
                (this.customInputs.map((x) => x.type).includes(type) &&
                    ["fullname", "email", "subject", "language", "cityAndDistrict"].includes(type)) ||
                this.disableButton
            );
        },
        getFieldDescription(type) {
            let result = "";
            if (type === "text") result = this.$t("message.textField");
            else if (type === "label") result = this.$t("message.labelField");
            else if (type === "numeric") result = this.$t("message.numericField");
            else if (type === "phone") result = this.$t("message.phoneField");
            else if (type === "email") result = this.$t("message.emailField");
            else if (type === "subject") result = this.$t("message.departmentField");
            else if (type === "select") result = this.$t("message.selectField");
            else if (type === "fullname") result = this.$t("message.fullnameField");
            else if (type === "city") result = this.$t("message.city");
            else if (type === "cityAndDistrict") result = this.$t("message.cityDistrict");

            return result;
        },
    },
    beforeDestroy() {
        if (this.workaroundTimerId) clearInterval(this.workaroundTimerId);
    },
    mounted() {
        this.getCustomLoginFormInputs();
        //Workaround for bug: wsyg editöründe link ekleme formunda tıklama çalışmıyordu:https://softcand.atlassian.net/browse/SUP-1390
        const self = this;
        this.workaroundTimerId = setInterval(() => {
            const elems = Array.from(document.querySelectorAll("div.editr--toolbar   input"));
            if (elems.length) {
                const cb = (e) => e.target.focus();
                const oldCb = self.linkInputWorkAroundCallback;
                Array.from(document.querySelectorAll("div.editr--toolbar   input")).forEach((x) => {
                    if (oldCb) x.removeEventListener("click", oldCb);
                    x.addEventListener("click", cb);
                    x.workaroundCallbackAdded = true;
                });
                self.linkInputWorkAroundCallback = cb;
            }
        }, 1000);
        //End of workaround
    },
    updated() {
        log("updated");
    },
};
</script>

<style lang="scss" scoped>
.feature {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 16px 20px;
    max-width: 900px;
    transition: all 0.3s ease;

    .left {
        display: flex;
        align-items: center;
        gap: 12px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1;

        .feature-title {
            font-size: 15px;
            font-weight: 500;
            color: var(--color-text-strong-default);
            display: flex;
            align-items: center;
            gap: 8px;

            .md-icon {
                font-size: 20px;
                opacity: 0.6;
                color: #6b7280;
            }

            span {
                color: #374151;
            }
        }

        .feature-description {
            font-size: 14px;
            font-weight: 400;
            color: var(--color-text-low-default);
        }
    }

    .right {
        .actions {
            display: flex;
            gap: 6px;
        }
    }
}

#customLoginForm {
    margin-bottom: 10%;
    padding: 2rem;
    min-height: 600px;

    .col-lg-4 {
        border-right: 1px solid #e5e7eb !important;
        padding-right: 24px;
    }

    .col-lg-8 {
        padding-left: 24px;
    }

    #list {
        height: 100%;
        min-height: 400px;
    }

    .fields,
    .addButton {
        background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
        border-radius: 12px;
        margin-bottom: 12px !important;
        cursor: pointer;
        padding: 6px 14px;
        user-select: none;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);
        border: 1px solid #e5e7eb;
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;
        overflow: hidden;

        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 4px;
            height: 100%;
            background: linear-gradient(180deg, #3b82f6 0%, #2563eb 100%);
            opacity: 0;
            transition: opacity 0.25s ease;
        }

        &:hover::before {
            opacity: 1;
        }
    }

    .fields {
        display: flex;
        align-items: center;
        min-width: 100%;
        padding: 14px 18px;

        &:hover {
            cursor: move;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 6px rgba(0, 0, 0, 0.06);
            border-color: #3b82f6;
            background: #ffffff;
        }

        &:active {
            transform: translateY(0);
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .infoF {
            flex: 1;
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 14px;
            font-weight: 500;
            color: #1f2937;

            .md-icon {
                color: #6b7280;
                font-size: 20px;
                cursor: grab;
                transition: color 0.2s ease;

                &:active {
                    cursor: grabbing;
                }
            }

            span {
                line-height: 1.5;

                &:first-of-type {
                    color: #1f2937;
                    font-weight: 600;
                }

                &:last-of-type {
                    color: #6b7280;
                    font-weight: 500;
                    font-size: 13px;
                }
            }

            &:hover .md-icon {
                color: #3b82f6;
            }
        }

        .actions {
            display: flex;
            gap: 4px;
            min-height: 42px;
            align-items: center;

            button {
                min-width: 36px;
                width: 36px;
                height: 36px;
                border-radius: 8px !important;
                transition: all 0.2s ease;
                background: #f9fafb;
                margin: 0 !important;

                &:hover {
                    transform: scale(1.05);
                    background: #f3f4f6;
                }

                &:active {
                    transform: scale(0.95);
                }

                .md-icon {
                    font-size: 20px;
                }
            }
        }
    }

    .addButton {
        border: 2px dashed #cbd5e1;
        width: 100%;
        text-align: center;
        padding: 16px;
        font-weight: 600;
        color: #6b7280;
        background: transparent;
        transition: all 0.25s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;

        strong {
            font-size: 20px;
            color: #3b82f6;
        }

        &:hover {
            border-color: #3b82f6;
            background: rgba(59, 130, 246, 0.02);
            color: #3b82f6;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
        }

        &:active {
            transform: translateY(0);
        }
    }

    .md-button.fields {
        background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
        border-radius: 12px;
        padding: 14px 18px;
        margin-bottom: 10px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
        border: 1px solid #e5e7eb;
        transition: all 0.25s ease;
        text-align: left;
        font-weight: 500;
        color: #374151;

        &:hover {
            transform: translateX(4px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            border-color: #3b82f6;
            background: linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%);
        }

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
            transform: none !important;
        }

        .md-icon {
            color: #3b82f6;
            font-size: 20px;
        }

        span {
            font-size: 14px;
            margin-left: 8px;
        }
    }
}
</style>

<style lang="scss">
.md-button.fields .md-ripple {
    justify-content: start;
}

#newFieldModal {
    .ant-modal {
        top: 5rem;
        width: 650px !important;
    }

    #cancelButton {
        flex-grow: 0 !important;
    }

    #inner-modal {
        overflow-y: scroll;
        overflow-x: hidden;
        max-height: 70vh;
    }

    .ant-modal-wrap {
        max-height: 100vh;
        overflow: hidden;
    }
}

#buttons {
    display: flex;
    justify-content: flex-end;
    gap: 7px;
}
</style>
